import styled from '@emotion/styled';
import { useEffect } from 'react';
import { ValuesType } from 'utility-types';
import { MenuButton } from '~/modules/Elements/Menu';
import events from '~/modules/GameEvents/GameEvents';
import { useEventListenerSelector } from '~/modules/GameEvents/hooks';
import PlayersManager from '~/modules/Players/PlayersManager';
import UserMediaEnabled from '~/modules/UserMedia/UserMediaEnabled';
import { useMicrophoneStatus } from '~/modules/UserMedia/hooks';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import isChromium from '~/modules/utils/isChromium';
import isWindows from '~/modules/utils/isWindows';
import ConnectRemoteMic from '~/routes/ConnectRemoteMic/ConnectRemoteMic';
import { MicrophoneInputSource } from '~/routes/SelectInput/InputSources/Microphone';
import PlayerSelector from '~/routes/SelectInput/Variants/Components/PlayerSelector';
import { useMicrophoneList } from '~/routes/SelectInput/hooks/useMicrophoneList';
import usePlayerNumberPreset from '~/routes/SelectInput/hooks/usePlayerNumberPreset';
import { useRemoteMicAutoselect } from '~/routes/SelectInput/hooks/useRemoteMicAutoselect';
import { MicSetupPreference } from '~/routes/Settings/SettingsState';

interface Props {
  onSetupComplete: (complete: boolean) => void;
  onBack: () => void;
  onSave: () => void;
  closeButtonText: string;
  changePreference: (pref: ValuesType<typeof MicSetupPreference>) => void;
}

function Advanced(props: Props) {
  usePlayerNumberPreset(2);
  const inputs = useMicrophoneList(true);
  const status = useMicrophoneStatus();

  useEffect(() => {
    props.onSetupComplete(status === 'accepted');
  }, [status]);

  useRemoteMicAutoselect();

  const { register } = useKeyboardNav({ onBackspace: props.onBack });

  const selectedInputs = useEventListenerSelector(events.playerInputChanged, () => PlayersManager.getInputs());
  const micInputs = selectedInputs.filter((input) => input.source === MicrophoneInputSource.inputName);

  const players = useEventListenerSelector(
    [events.inputListChanged, events.playerInputChanged, events.playerRemoved],
    () => PlayersManager.getPlayers(),
  );

  return (
    <Wrap>
      <UserMediaEnabled fallback={<h2>Please allow access to the microphone so we can show them.</h2>}>
        <ConnectRemoteMic />

        <Selectors>
          {players.map((player, index) => (
            <PlayerSelector
              inputs={inputs}
              player={player}
              key={player.number}
              register={register}
              readonly={index > 1}
            />
          ))}
        </Selectors>

        <hr />

        {micInputs.length > 1 && isChromium() && isWindows() && (
          <WarningText>
            <strong>Chrome</strong> is known for not handling SingStar mics well. If you notice any problems, try using
            an alternative browser (eg. <strong>MS Edge</strong> or <strong>Firefox</strong>)
          </WarningText>
        )}
      </UserMediaEnabled>

      <Buttons>
        <MenuButton {...register('back-button', props.onBack)}>Change Input Type</MenuButton>
        <MenuButton {...register('save-button', props.onSave)}>{props.closeButtonText}</MenuButton>
      </Buttons>
    </Wrap>
  );
}

const Wrap = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Selectors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  * {
    min-width: 0;
  }
`;

const WarningText = styled.h3`
  line-height: 1.25;

  @media (max-width: 520px) {
    font-size: 1.2rem;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

export default Advanced;
